import { ReactElement, ReactNode } from 'react';
import Link from 'components/UI/Link';
import RichText from 'components/UI/Richtext';
import { IComponentServicesListFields, IFragmentService } from 'types/contentful';

export default function ServicesList({
  id,
  heading,
  copy,
  services = [],
}: IComponentServicesListFields): ReactElement {
  function LinkTag({
    hasLink,
    slug,
    children,
  }: {
    hasLink: boolean;
    slug: string;
    children: ReactNode;
  }): ReactElement {
    if (hasLink) {
      return <Link slug={slug}>{children}</Link>;
    }
    return <span>{children}</span>;
  }

  return (
    <div id={id} className="pb-10 md:pb-20 px-site-component">
      <div className="flex flex-col lg:flex-row justify-center gap-8 lg:gap-6">
        {heading && (
          <div className="w-full lg:w-1/2">
            <div className="bordered-heading-richtext text-left text-primary-actual md:mb-8 font-light">
              <RichText
                document={heading}
                classNames="text-new-34 md:text-4xl lg:leading-new-43 max-w-none richtext-nomargin-headings richtext-font-inherit"
                noEmpty={true}
              />
            </div>
          </div>
        )}
        {copy && (
          <div className="w-full lg:w-1/2 lg:pl-6">
            <RichText document={copy} classNames="text-lg text-primary-actual text-left" />
          </div>
        )}
      </div>

      {(heading || copy) && <div className="block mb-8 lg:mb-16"></div>}

      <ul className="list-none grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-4 gap-x-6">
        {services.map((service: IFragmentService) => {
          const { service: serviceName, slug } = service.fields;
          return (
            <li key={service.sys.id} className="h-full">
              <LinkTag hasLink={!!slug} slug={`/${slug}`}>
                <a className="text-lg teal-fontcolor hover:text-primary-actual bg-neutral-alt hover:bg-neutral-alt/30 p-2.5 flex items-center justify-start gap-3 h-full">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M0.833008 9.99967C0.833008 9.99967 4.16634 3.33301 9.99967 3.33301C15.833 3.33301 19.1663 9.99967 19.1663 9.99967C19.1663 9.99967 15.833 16.6663 9.99967 16.6663C4.16634 16.6663 0.833008 9.99967 0.833008 9.99967Z"
                        stroke="white"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                        stroke="white"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="flex-grow">{serviceName}</span>
                  {!!slug && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="19"
                      viewBox="0 0 11 19"
                      fill="none"
                    >
                      <path
                        d="M1.33301 1.33301L9.33301 9.33301L1.33301 17.333"
                        stroke="#00C1D4"
                        strokeWidth="2.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </a>
              </LinkTag>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
